import type { MeetingTemplateKind } from '@wundo/shared/meetingTemplate.ts';
import { Button } from '../button/button.tsx';
import { SettingsSection } from '../settings/settings-section.tsx';
import { TypePill } from '../type-pill/type-pill.tsx';
import type { MeetingTemplateDraft } from './meeting-template-types.ts';
import {
  DEFAULT_MEETING_TEMPLATE_VIEW_LABELS,
  type MeetingTemplateViewLabels,
} from './meeting-template-view-labels.ts';
import { templateColorVariant } from './template-color-variant.ts';

export interface MeetingTemplateViewProps {
  template: MeetingTemplateDraft;
  /** Absent, the view says nothing about kind. */
  kind?: MeetingTemplateKind;
  onBack?: () => void;
  labels?: Partial<MeetingTemplateViewLabels>;
}

/**
 * A template as a reader sees it: shown as text rather than as a form nobody
 * may fill in. Knowing what the organisation agreed on is why they opened it.
 */
export function MeetingTemplateView({
  template,
  kind,
  onBack,
  labels,
}: MeetingTemplateViewProps) {
  const text = { ...DEFAULT_MEETING_TEMPLATE_VIEW_LABELS, ...labels };

  return (
    <>
      {onBack && (
        <div className='mb-4'>
          <Button size='sm' variant='ghost' onClick={onBack}>
            {text.back}
          </Button>
        </div>
      )}

      <div className='mb-6 flex flex-col items-start gap-2'>
        {/* The name is the title here, so the pill wears a title's size. */}
        <TypePill
          type={templateColorVariant(template.color)}
          className='px-3.5 py-1.5 text-base'
        >
          {template.name}
        </TypePill>
        {template.description && (
          <p className='text-base text-ink'>{template.description}</p>
        )}
        {kind && (
          <p className='text-sm text-ink-body'>
            {text.kindLabel}: {text.kinds[kind]}
          </p>
        )}
        <p className='text-sm text-ink-muted'>{text.readOnlyNote}</p>
      </div>

      <SettingsSection title={text.topicsTitle}>
        {template.sections.map((section, index) => (
          <div
            key={index}
            className='flex flex-col gap-1 border-t border-fill px-5.5 py-4 first:border-t-0'
          >
            <p className='text-sm font-medium text-ink'>
              {index + 1}. {section.title}
            </p>
            {section.description && (
              <p className='text-sm text-ink-muted'>{section.description}</p>
            )}
          </div>
        ))}
      </SettingsSection>
    </>
  );
}
